'use client';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useState, useTransition } from 'react';
import { useT } from '../../lib/i18n';
import { Icon } from '../Icon';
import { Working } from '../Working';

/**
 * 게시판 공용 조각 — 검색바·페이저·본문.
 *
 * 목록은 서버가 그리고(공지·FAQ 는 검색 노출이 매출로 이어진다), **주소가 상태**다.
 * 검색·페이지를 주소에 담으므로 뒤로 가기·북마크·공유가 그대로 동작한다.
 */
const ICON_SEARCH = 'search';
const ICON_PREV = 'chevron-left';
const ICON_NEXT = 'chevron-right';

/** 지금 주소에 값 하나를 얹어 새 주소를 만든다 — 비우면 뺀다 */
function withParam(pathname: string, params: URLSearchParams, patch: Record<string, string | null>) {
  const next = new URLSearchParams(params);
  for (const [k, v] of Object.entries(patch)) {
    if (v == null || v === '') next.delete(k);
    else next.set(k, v);
  }
  const q = next.toString();
  return q ? `${pathname}?${q}` : pathname;
}

/**
 * 검색바 — 가이드 `bbs-search-bar`.
 * `types` 를 주면 왼쪽에 셀렉트가 붙는다 — 1:1 문의의 **문의 유형**이다.
 * 유형 값은 코드표(`tb_code.qna_option`·`tq_option`)에서 오고 주소의 `option` 에 담긴다.
 */
export function BoardSearch({ placeholder, types }: {
  placeholder: string;
  types?: { value: string; label: string }[];
}) {
  const t = useT();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [text, setText] = useState(params.get('q') ?? '');
  const [type, setType] = useState(params.get('option') ?? '');
  /*
   * 검색은 **주소를 바꾸는 이동**이라 서버가 다시 그리는 동안 아무 표시가 없었다(2026-09-05
   * 사용자 지적 "검색 눌렀을 때 로딩중 안 나옴"). 이동을 transition 으로 감싸 그동안
   * [검색] 버튼이 작업 중 표시(점 셋)를 낸다 — 주 액션 버튼의 공용 규칙 그대로다.
   */
  const [pending, start] = useTransition();

  // 검색하면 1쪽으로 돌아간다 — 3쪽을 보다 검색하면 결과가 3쪽뿐일 수 있다
  const go = () => start(() => router.push(withParam(pathname, params, { q: text.trim() || null, option: type || null, page: null })));

  return (
    <div className="bbs-search-bar mb15">
      <div className="bbs-search-inner">
        {/* 유형은 고르면 바로 걸린다 — [검색]을 또 누르라고 하기엔 한 번의 조작이면 끝이다 */}
        {types && (
          <select className="form-select bbs-search-type" value={type}
            onChange={(e) => {
              setType(e.target.value);
              router.push(withParam(pathname, params, { option: e.target.value || null, page: null }));
            }}>
            {types.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        <div className="bbs-search-input-wrap">
          <Icon name={ICON_SEARCH} />
          <input type="text" className="bbs-search-input" placeholder={placeholder}
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') go(); }} />
        </div>
        <button type="button" className="action-btn action-submit btn-sm bbs-search-btn" disabled={pending} onClick={go}>
          {pending ? <Working inline /> : null}{t('bbs.search')}
        </button>
      </div>
    </div>
  );
}

/** 보여 줄 페이지 번호 — 현재 주변 다섯 칸(강좌 목록과 같은 규칙) */
function pageNumbers(page: number, total: number): number[] {
  const start = Math.max(1, Math.min(page - 2, total - 4));
  const end = Math.min(total, start + 4);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * 페이저 — 가이드 `.pagination`.
 * **감싸개(`d-flex jc-center`)를 빼면 왼쪽에 붙는다** — `.pagination` 자체엔 정렬이 없다.
 */
export function BoardPager({ page, total, size }: { page: number; total: number; size: number }) {
  const t = useT();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const pages = Math.max(1, Math.ceil(total / size));
  /*
   * **글이 하나도 없을 때만 뺀다**(2026-09-01 사용자 지적 "페이지네이션도 없고").
   * 전에는 한 쪽뿐이면 감췄는데, 가이드는 한 쪽이어도 그린다 — 목록 아래가 허전해지고
   * "여기가 끝인가" 를 알려 주는 표시가 사라진다.
   */
  if (!total) return null;
  const go = (n: number) => router.push(withParam(pathname, params, { page: n <= 1 ? null : String(n) }));

  return (
    <div className="d-flex jc-center my20">
      <div className="pagination">
        {/* 끝에서는 잠근다 — 유효성이 아니라 갈 곳이 없는 것이라 할 말이 없다 */}
        <button className="page-btn" disabled={page <= 1} onClick={() => go(page - 1)} aria-label={t('bbs.prevPage')}>
          <Icon name={ICON_PREV} />
        </button>
        {pageNumbers(page, pages).map((n) => (
          <button key={n} className={`page-btn${n === page ? ' active' : ''}`} onClick={() => go(n)}>{n}</button>
        ))}
        <button className="page-btn" disabled={page >= pages} onClick={() => go(page + 1)} aria-label={t('bbs.nextPage')}>
          <Icon name={ICON_NEXT} />
        </button>
      </div>
    </div>
  );
}
